import React, { useEffect, useRef, useState } from "react";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import { walletRepository } from "../../../repositories/WalletRepository";
import { QRCodeCanvas } from "qrcode.react";
import Invoice, { InvoiceHandle } from "./components/Invoice";
import { formatAmountWithSATS } from "../../../utils/Utils";
import { useWalletTransactions } from "../../../hooks/useWalletTransactions";
import { TransactionStatus } from "../../../models/Transactions";
import { CopyToClipboard } from "../../../components/CopyToClipboard";
import { useWallet } from "../../../hooks/useWallet";

const CreateInvoiceRecap: React.FC = () => {
    const navigate = useNavigate();
    const {transactions, refreshTransaction} = useWalletTransactions();
    const invoiceRef = useRef<InvoiceHandle>(null);
    const [loading, setLoading] = useState(true);
    const [invoice, setInvoice] = useState('');
    const [paymentHash, setPaymentHash] = useState<string | null>(null);
    const location = useLocation();
    const amount = location.state?.amount as number;
    const {createInvoice} = useWallet();

    useEffect(() => {
        if(amount && amount > 0) {
            createInvoice(amount)
                .then((invoice) => {
                    if(invoice?.invoice) {
                        setInvoice(invoice.invoice);
                        setPaymentHash(invoice.paymentHash ?? null);
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
        } else {
            navigate('/home/wallet/receive/create-invoice/amount');
        }
    }, []);

    useEffect(() => {
        if(paymentHash) {
            refreshTransaction(paymentHash)
                .catch((error) => {
                    console.error(error);
                });
            
            const intervalId = setInterval(() => {
                refreshTransaction(paymentHash)
                    .catch((error) => {
                        console.error(error);
                    });
            }, 3000);
            
            return () => {
                clearInterval(intervalId);
            };
        }
    }, [paymentHash, refreshTransaction]);

    useEffect(() => {
        const transaction = transactions.find((transaction) => (transaction.invoice === invoice || transaction.paymentHash === paymentHash) && transaction.status === TransactionStatus.SUCCESS);
        if(transaction) {
            navigate('/home/wallet/tx/' + transaction.paymentHash, { state: { template: 'success', markAsReadTimeout: 2000 } });
        }
    }, [transactions]);

    return (
        <LayoutWithTopBar
            breadcrumbs={[
                { title: 'Wallet', onClick: () => navigate('/home/wallet') },
                { title: 'Receive', onClick: () => navigate('/home/wallet/receive') },
                { title: 'Create invoice' }
            ]}
            onClickPrevArrow={() => navigate('/home/wallet/receive/create-invoice/amount')}
        >
            {
                loading ? (
                    <Loader />
                ) : (
                    <div className="flex-1 px-4 flex flex-col justify-center items-center">
                        <div className={`flex flex-col items-stretch gap-6 max-w-[500px] w-full justify-center`}>
                            <div className="bg-brand-bg-2 rounded-xl p-4 flex flex-col gap-4">
                                <div className="flex justify-center">
                                    <div className="bg-brand-white rounded-lg p-4 flex justify-center items-center relative">
                                        <QRCodeCanvas
                                            value={invoice}
                                            size={220}
                                            bgColor="#FFFFFF"
                                            fgColor="#000000"
                                            level="H"  // Error correction level: L, M, Q, H
                                            style={{ 
                                                maxWidth: '100%',
                                                height: 'auto',
                                                backgroundColor: "#FFFFFF",
                                                filter: "none",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => {
                                                invoiceRef.current?.handleCopy();
                                            }}
                                        />
                                        <img 
                                            src="/logo192.png" // Adjust path to your logo
                                            alt="Logo"
                                            className="absolute w-[67px] h-[67px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-white   p-1 cursor-pointer"
                                            onClick={() => {
                                                invoiceRef.current?.handleCopy();
                                            }}
                                        />
                                    </div>
                                </div>
                                
                                <Invoice 
                                    ref={invoiceRef}
                                    invoice={invoice} 
                                    className="justify-center" 
                                />
                            </div>

                            <div className="bg-brand-bg-2 rounded-xl p-4 flex flex-col">
                                <div className="flex flex-row justify-between">
                                    <p className="text-brand-text-2">Amount</p>
                                    <p className="">{formatAmountWithSATS(amount)}</p>
                                </div>
                            </div>

                            <div className="flex flex-row justify-center items-center">
                                <CopyToClipboard 
                                    toCopy={invoice} 
                                    className="bg-brand-orange hover:opacity-90 text-white rounded-full h-[40px] text-sm font-medium text-lg transition-colors rounded-full flex items-stretch justify-center"
                                    classNameChildren="flex flex-row items-center justify-center min-w-[200px]"
                                    classNameCopied="text-center"
                                >
                                    <div className="flex flex-row items-center justify-center gap-2">
                                        <i className="material-icons text-[18px]" title="Copy to clipboard">file_copy</i>
                                        <span className="text-brand-text-2">Copy invoice</span>
                                    </div>
                                </CopyToClipboard>
                            </div>
                        </div>
                    </div>
                )
            }
        </LayoutWithTopBar>
    );
}

export default CreateInvoiceRecap;